import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GlobeIcon, UserIcon } from '@components/common/ui/icons';
import { ConnectionStatus } from '@hooks/useConnectionStatus';
import * as styles from '@components/notes/NoteViewer.module.css';

const STATUS_CONFIG: Record<ConnectionStatus, { label: string; dotClass: string }> = {
    connected: { label: 'editor.synced', dotClass: styles.dotConnected },
    connecting: { label: 'editor.connecting', dotClass: styles.dotConnecting },
    offline: { label: 'editor.offline', dotClass: styles.dotOffline },
};

interface EditorBottomBarProps {
    wordCount: number;
    readingTime?: number;
    isPublic?: boolean;
    ownerInfo?: { login?: string; name?: string } | null;
    ownerId?: string;
    connStatus?: ConnectionStatus;
}

export const EditorBottomBar: React.FC<EditorBottomBarProps> = ({
    wordCount,
    readingTime,
    isPublic = false,
    ownerInfo,
    ownerId,
    connStatus,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const statusCfg = connStatus ? STATUS_CONFIG[connStatus] : null;

    return (
        <div className={styles.bottomBar}>
            <div className={styles.bottomBarLeft}>
                <span>
                    {t('editor.words', { count: wordCount })}
                    {readingTime !== undefined ? ` · ${t('editor.minRead', { count: readingTime })}` : ''}
                </span>
                <span className={styles.bottomBarSeparator}>•</span>
                <span className={styles.bottomBarEditable}>{t('editor.canEdit')}</span>
                {isPublic && (
                    <>
                        <span className={styles.bottomBarSeparator}>•</span>
                        <span className={styles.publicBadge} title={t('topbar.publicNote')}>
                            <GlobeIcon className={styles.publicIcon} />
                            <span>{t('topbar.public')}</span>
                        </span>
                    </>
                )}
                {ownerInfo && (
                    <>
                        <span className={styles.bottomBarSeparator}>•</span>
                        <button
                            className={styles.ownerButton}
                            onClick={() => {
                                const identifier = ownerInfo.login || ownerId;
                                navigate(`/user/${identifier}`);
                            }}
                            title={t('topbar.ownerProfile', {
                                owner: ownerInfo.name || ownerInfo.login || t('topbar.ownerFallback'),
                            })}
                        >
                            <UserIcon className={styles.ownerIcon} />
                            <span>{ownerInfo.name || ownerInfo.login}</span>
                        </button>
                    </>
                )}
            </div>
            <div className={styles.bottomBarRight}>
                {statusCfg ? (
                    <span className={styles.autoSaved} title={statusCfg.label}>
                        <span className={`${styles.autoSavedDot} ${statusCfg.dotClass}`} />
                        {t(statusCfg.label)}
                    </span>
                ) : (
                    <span className={styles.autoSaved}>
                        <span className={styles.autoSavedDot} />
                        {t('editor.autoSaved')}
                    </span>
                )}
            </div>
        </div>
    );
};
